import { ApplicationState } from '@/store';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getAgentAvailableUniversities, isAgentOrAgencyRole } from '../roles/permissions';
import { IUrls } from 'types';

/**
 * Custom hook to get the list of universities available to an agent
 * This consolidates the logic that was previously duplicated in the
 * Create Application drawer and institution dropdown components
 */
export const useAgentUniversities = () => {
  const { role, authAgencyData } = useSelector(({ auth }: ApplicationState) => ({
    role: auth.role,
    authAgencyData: auth.authAgencyData,
  }));

  const configUrls = useSelector(({ config }: ApplicationState) => (config.urls || {}) as IUrls);
  const configUniIds = useSelector(({ config }: ApplicationState) => config.universityIds || []);

  // Only process agent-specific universities if the user is an agent
  const universities = useMemo(() => {
    if (isAgentOrAgencyRole(role)) {
      return getAgentAvailableUniversities(authAgencyData, configUniIds, configUrls);
    }

    // For non-agents, return all universities
    return configUniIds;
  }, [role, authAgencyData?.assignedUnis, authAgencyData?.blockCreateApplicationUnis, configUniIds, configUrls]);

  return {
    universities,
    isAgent: isAgentOrAgencyRole(role),
    hasAccessToApply: authAgencyData?.hasAssignedUniWithApplyEnabled ?? false,
    isLoading: isAgentOrAgencyRole(role) && !authAgencyData && configUniIds.length > 0,
  };
};
